import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  generateTeamColorMap, 
  generateTeamLegendData, 
  getTeamStatistics,
  COLOR_MODES 
} from '../utils/featureTeamColors';
import './FeatureTeamFilter.css';

// Définition des types de nœuds et leurs couleurs pour la légende
const NODE_TYPE_LEGEND = [
  { type: 'featureTeam', color: '#f078d6', border: '#f120c4', label: 'Feature Team' },
  { type: 'feature', color: '#4CAF50', border: '#2E7D32', label: 'Feature' },
  { type: 'component', color: '#2196F3', border: '#1565C0', label: 'Component' },
  { type: 'api', color: '#FFC107', border: '#FFA000', label: 'API' },
  { type: 'service', color: '#9C27B0', border: '#7B1FA2', label: 'Service' },
  { type: 'user', color: '#F44336', border: '#C62828', label: 'User' },
  { type: 'module', color: '#3F51B5', border: '#303F9F', label: 'Module' },
  { type: 'testNode', color: '#f4f5f4ff', border: '#0aee1dff', label: 'Node with Tests 🧪' }
];

const FeatureTeamLegend = ({ showLegend }) => {
  const { t } = useTranslation();
  console.log('FeatureTeamLegend visibility:', showLegend);
  if (!showLegend) return null;

  return (
    <div className="compact_legend">
      <div className="legend-title">{t('legendTitle')}</div>
      {NODE_TYPE_LEGEND.map(({ label, color, border }) => (
        <div key={label} className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: color, borderColor: border }}
          ></span>
          {label}
        </div>
      ))}
    </div>
  );
};

const FeatureTeamFilter = ({
  nodes = [],
  colorMode = COLOR_MODES.TYPE,
  onColorModeChange,
  selectedTeams = [],
  onTeamSelectionChange,
  showLegend = true,
  onShowLegendChange,
  compact = false
}) => {
  const { t } = useTranslation();

  console.log('FeatureTeamFilter render:', { showLegend, compact, nodesCount: nodes.length });
  
  // Generate team data
  const teamColorMap = useMemo(() => generateTeamColorMap(nodes), [nodes]);
  const legendData = useMemo(() => generateTeamLegendData(nodes), [nodes]);
  const statistics = useMemo(() => getTeamStatistics(nodes), [nodes]);
  
  // Handle color mode change
  const handleColorModeChange = (mode) => {
    if (onColorModeChange) {
      onColorModeChange(mode);
    }
  };

  // Handle team selection
  const handleTeamToggle = (team) => {
    const newSelection = selectedTeams.includes(team)
      ? selectedTeams.filter(t => t !== team)
      : [...selectedTeams, team];
    
    if (onTeamSelectionChange) {
      onTeamSelectionChange(newSelection);
    }
  };

  // Handle select all / clear all
  const handleSelectAll = () => {
    const allTeams = legendData.map(item => item.team);
    if (onTeamSelectionChange) {
      onTeamSelectionChange(allTeams);
    }
  };

  const handleClearAll = () => {
    if (onTeamSelectionChange) {
      onTeamSelectionChange([]);
    }
  };

  const colorModeOptions = [
    { 
      value: COLOR_MODES.TYPE, 
      label: t('featureTeamFilter.colorModes.type', 'By Type'),
      shortLabel: 'Type',
      icon: '🏷️'
    },
    { 
      value: COLOR_MODES.TEAM, 
      label: t('featureTeamFilter.colorModes.team', 'By Team'),
      shortLabel: 'Team',
      icon: '👥'
    },
    { 
      value: COLOR_MODES.COMBINED, 
      label: t('featureTeamFilter.colorModes.combined', 'Combined'),
      shortLabel: 'Mix',
      icon: '🎨'
    }
  ];

  // Compact horizontal action row for bottom-panel integration
  if (compact) {
    return (
      <div className="feature-team-filter compact-filter compact-filter-panel">
        <div className="compact-filter-row">
          {colorModeOptions.map(option => (
            <button
              key={option.value}
              className={`compact-btn ${colorMode === option.value ? 'active' : ''}`}
              onClick={() => handleColorModeChange(option.value)}
              title={option.label}
            >
              {option.icon} {option.shortLabel}
            </button>
          ))}
          
          <div className="separator" />
          
          <div className="team-dropdown-wrapper">
            <select
              className="team-dropdown"
              value=""
              onChange={(e) => {
                const action = e.target.value;
                if (action === 'all') handleSelectAll();
                else if (action === 'none') handleClearAll();
                else if (action) handleTeamToggle(action);
                e.target.value = ''; // Reset dropdown
              }}
            >
              <option value="">
                Teams ({selectedTeams.length === 0 ? 'All' : selectedTeams.length})
              </option>
              <option value="all">✓ Select All</option>
              <option value="none">✗ Clear All</option>
              <option disabled>───────────</option>
              {legendData.map(item => {
                const isSelected = selectedTeams.includes(item.team) || selectedTeams.length === 0;
                return (
                  <option key={item.team} value={item.team}>
                    {isSelected ? '✓' : '○'} {item.team} ({item.count})
                  </option>
                );
              })}
            </select>
          </div>

          <button
            className={`compact-btn legend-toggle ${showLegend ? 'active' : ''}`}
            onClick={() => {
              console.log('Toggle clicked, current showLegend:', showLegend);
              const newValue = !showLegend;
              console.log('Setting showLegend to:', newValue);
              onShowLegendChange && onShowLegendChange(newValue);
            }}
            title={showLegend ? "Hide types legend" : "Show types legend"}
          >
            {showLegend ? '👁️' : '👁️‍🗨️'} Types
          </button>
        </div>

        {/* Légende des types intégrée - HTML original qui fonctionnait */}
        {showLegend && <FeatureTeamLegend showLegend={showLegend} />}
      </div>
    );
  }

  return (
    <div className={`feature-team-filter ${compact ? 'compact-filter' : ''}`}>
      <div className="filter-header-filter">
        <h3 className="filter-title-filter">
          👥 {compact ? t('featureTeamFilter.teams', 'Teams') : t('featureTeamFilter.title', 'Feature Teams')}
        </h3>
        {!compact && (
          <div className="filter-stats-filter">
            <span className="stat-item-filter">
              <strong>{statistics.totalNodes}</strong> nodes
            </span>
            <span className="stat-item-filter">
              <strong>{statistics.totalTeams}</strong> teams
            </span>
          </div>
        )}
      </div>

      {/* Color Mode Selection */}
      <div className="color-mode-section-filter">
        <h4 className="section-title-filter">
          {t('featureTeamFilter.colorMode', 'Color Mode')}
        </h4>
        <div className="color-mode-tabs-filter">
          {colorModeOptions.map(option => (
            <button
              key={option.value}
              className={`color-mode-tab-filter ${colorMode === option.value ? 'active-filter' : ''}`}
              onClick={() => handleColorModeChange(option.value)}
              title={option.label}
            >
              <span className="tab-icon-filter">{option.icon}</span>
              <span className="tab-label-filter">{compact ? option.shortLabel : option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Team Selection */}
      <div className="team-selection-section-filter">
        <div className="section-header-filter">
          <h4 className="section-title-filter">
            {t('featureTeamFilter.teamFilter', 'Filter by Teams')}
          </h4>
          <div className="selection-actions-filter">
            <button 
              className="action-btn-filter select-all-filter"
              onClick={handleSelectAll}
              disabled={selectedTeams.length === legendData.length}
            >
              {t('featureTeamFilter.selectAll', 'All')}
            </button>
            <button 
              className="action-btn-filter clear-all-filter"
              onClick={handleClearAll}
              disabled={selectedTeams.length === 0}
            >
              {t('featureTeamFilter.clearAll', 'None')}
            </button>
          </div>
        </div>

        <div className="team-filters-filter">
          {legendData.map(item => (
            <div 
              key={item.team}
              className={`team-filter-item-filter ${selectedTeams.includes(item.team) || selectedTeams.length === 0 ? 'selected-filter' : 'unselected-filter'}`}
            >
              <label className="team-filter-label-filter">
                <input
                  type="checkbox"
                  checked={selectedTeams.includes(item.team) || selectedTeams.length === 0}
                  onChange={() => handleTeamToggle(item.team)}
                  className="team-checkbox-filter"
                />
                <span 
                  className="team-color-indicator-filter"
                  style={{ backgroundColor: item.color }}
                />
                <div className="team-info-filter">
                  <span className="team-name-filter">{item.team}</span>
                  <span className="team-count-filter">({item.count})</span>
                </div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Legend Toggle */}
      <div className="legend-toggle-section-filter">
        <label className="legend-toggle-label-filter">
          <input
            type="checkbox"
            checked={showLegend}
            onChange={(e) => onShowLegendChange && onShowLegendChange(e.target.checked)}
            className="legend-checkbox-filter"
          />
          <span className="toggle-text-filter">
            {t('featureTeamFilter.showLegend', 'Show Legend')}
          </span>
        </label>
      </div>

      {/* Inline legacy legend for non-compact mode - with CSS classes */}
      {showLegend && (
        <div className="compact-legend-container">
          <div className="legend-title">Types :</div>
          <div className="legend-item">
            <span className="legend-color-dot feature-team-dot"></span>
            {t('graphView.legend.types.featureTeam')}
          </div>
          <div className="legend-item">
            <span className="legend-color-dot feature-dot"></span>
            {t('graphView.legend.types.feature')}
          </div>
          <div className="legend-item">
            <span className="legend-color-dot component-dot"></span>
            {t('graphView.legend.types.component')}
          </div>
          <div className="legend-item">
            <span className="legend-color-dot api-dot"></span>
            {t('graphView.legend.types.api')}
          </div>
          <div className="legend-item">
            <span className="legend-color-dot service-dot"></span>
            {t('graphView.legend.types.service')}
          </div>
          <div className="legend-item">
            <span className="legend-color-dot user-dot"></span>
            {t('graphView.legend.types.user')}
          </div>
          <div className="legend-item">
            <span className="legend-color-dot module-dot"></span>
            {t('graphView.legend.types.module')}
          </div>
          <div className="legend-item">
            <span className="legend-color-dot test-node-dot"></span>
            {t('graphView.legend.types.testNode')}
          </div>
        </div>
      )}

      {/* Active Filters Summary */}
      {selectedTeams.length > 0 && (
        <div className="active-filters-summary-filter">
          <div className="summary-header-filter">
            <span className="summary-title-filter">
              {t('featureTeamFilter.activeFilters', 'Active Filters')}
            </span>
            <span className="summary-count-filter">({selectedTeams.length})</span>
          </div>
          <div className="active-filters-filter">
            {selectedTeams.map(team => (
              <span 
                key={team}
                className="active-filter-tag-filter"
                style={{ 
                  backgroundColor: teamColorMap[team] + '20',
                  borderColor: teamColorMap[team],
                  color: teamColorMap[team]
                }}
              >
                {team}
                <button
                  className="remove-filter-filter"
                  onClick={() => handleTeamToggle(team)}
                  title={t('featureTeamFilter.removeFilter', 'Remove filter')}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureTeamFilter;